import { Typography } from "@material-ui/core";
import OverflowTooltipTypography from "apps/platform-client/client/src/components/OverflowTooltipTypography";
import { useMemo } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { organizationAgreementState, OrganizationCaselessAgreementsStatus } from "../../../..//store/layout";
import FormattedCounterparties from "../../../../components/FormattedCounterparties";
import { tableIdMinWidth } from "../../../../hooks/use-map-theme-styles";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../../models/common/data";
import { RefundStatus } from "../../../../models/generated/graphql";
import { Column } from "../../../../types/tableTypes";
import renderCurrency from "../../../../utils/currency";
import formatCurrency from "../../../../utils/currency-format";
import {
	getAdministrationOrPurchaseDateFieldName,
	getCaseOrAgreementIdFieldName,
	getPurchaseOrPatientFieldName,
} from "../../../../utils/getCaselessOrPatientFields";
import { localeDate } from "../../../../utils/locale-date";
import startUpper from "../../../../utils/start-upper";
import { getTagTranslationKey } from "../../../agreements/AgreementsTable";
import { GetRefundsQuery } from "../types/queries.generated";
import AdjustedAmountColumns from "./AdjustedAmountOrRateColumns";
import IdColumn from "./IdColumn";
import StatusColumn from "./StatusColumn";

type RefundColumn = Column<GetRefundsQuery["refundTableRows"][number]>;

export function getRefundColumns(
	t: TFunction<string>,
	organizationAgreementState: OrganizationCaselessAgreementsStatus["default"]
): Array<RefundColumn> {
	const { hasOnlyCaselessAgreements, hasOnlyPatientAgreements } = organizationAgreementState;

	return [
		{
			title: t("transaction-id"),
			field: "id",
			default: true,
			render: (rawData) => <IdColumn entityId={rawData.id} marginLeft="-15px" />,
		},
		{
			title: t("is-delta"),
			field: "isDelta",
			default: false,
			render: (rawData) => <OverflowTooltipTypography>{rawData.isDelta ? t("delta-Y") : t("delta-N")}</OverflowTooltipTypography>,
		},
		{
			title: startUpper(t("predecessorIds")),
			field: "predecessorIds",
			default: false,
			render: (rawData) => <OverflowTooltipTypography>{rawData.predecessorIds || EMPTY_VALUE_PLACEHOLDER}</OverflowTooltipTypography>,
		},
		{
			title: startUpper(t("successorIds")),
			field: "successorIds",
			default: false,
			render: (rawData) => <OverflowTooltipTypography>{rawData.successorIds || EMPTY_VALUE_PLACEHOLDER}</OverflowTooltipTypography>,
		},
		{
			title: getCaseOrAgreementIdFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			field: "caseOrAgreementId",
			default: true,
			render: (rawData) => (
				<OverflowTooltipTypography>{rawData.caseOrAgreementId || EMPTY_VALUE_PLACEHOLDER}</OverflowTooltipTypography>
			),
		},
		{
			title: t("status"),
			field: "status",
			default: true,
			render: (rawData) => {
				if (rawData.status === RefundStatus.Pending && rawData.currentReviewerOrganization) {
					return <StatusColumn status={rawData.status} text={`${t("in-review-of")} ${rawData.currentReviewerOrganization}`} />;
				}

				return <StatusColumn status={rawData.status!} />;
			},
		},
		{
			title: t("recordType"),
			field: "recordTypeLocalizationKey",
			default: false,
			render: (rawData) => <OverflowTooltipTypography>{t(rawData.recordTypeLocalizationKey || "")}</OverflowTooltipTypography>,
		},
		{
			title: getPurchaseOrPatientFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			field: "purchaseOrPatientId",
			default: true,
			render: (rawData) => <OverflowTooltipTypography>{rawData.purchaseOrPatientId}</OverflowTooltipTypography>,
		},
		{
			title: startUpper(t("documentId")),
			field: "documentId",
			default: true,
			render: (rawData) => <OverflowTooltipTypography>{rawData.documentId || EMPTY_VALUE_PLACEHOLDER}</OverflowTooltipTypography>,
		},
		{
			title: startUpper(t("invoiceId")),
			field: "invoiceNumber",
			default: false,
			render: (rawData) => <OverflowTooltipTypography>{rawData.invoiceNumber || ""}</OverflowTooltipTypography>,
		},
		{
			title: startUpper(t("insuranceClaimId")),
			field: "insuranceClaimId",
			default: false,
			render: (rawData) => <OverflowTooltipTypography>{rawData.insuranceClaimId || ""}</OverflowTooltipTypography>,
		},
		{
			title: startUpper(t("counterparties")),
			field: "counterParties",
			default: true,
			render: (rawData) => <FormattedCounterparties rowData={rawData} fieldAssessor="counterParties" />,
		},
		{
			title: startUpper(t("legalEntity")),
			field: "legalEntityName",
			default: false,
			render: (rawData) => <OverflowTooltipTypography>{rawData.legalEntityName || ""}</OverflowTooltipTypography>,
		},
		{
			title: t("agreement-tags"),
			field: "formattedTags",
			default: true,
			render: (rowData) => (
				<Typography style={{ minWidth: tableIdMinWidth }} variant="body2">
					{t(getTagTranslationKey(rowData.formattedTags!))}
				</Typography>
			),
		},
		{
			title: t("invoiceDate"),
			field: "invoiceDate",
			default: true,
			render: (rawData) => <OverflowTooltipTypography align="right">{localeDate(rawData.invoiceDate)}</OverflowTooltipTypography>,
		},
		{
			title: getAdministrationOrPurchaseDateFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			field: "prescriptionDate",
			default: true,
			render: (rawData) => (
				<OverflowTooltipTypography align="right">{localeDate(rawData.prescriptionDate)}</OverflowTooltipTypography>
			),
		},
		{
			title: t("transaction-last-adjusted-date"),
			field: "lastAdjustedAt",
			default: false,
			render: (rawData) => <OverflowTooltipTypography align="right">{localeDate(rawData.lastAdjustedAt)}</OverflowTooltipTypography>,
		},
		{
			title: t("pack"),
			field: "product",
			default: true,
			render: (rawData) => <OverflowTooltipTypography align="right">{rawData.product || ""}</OverflowTooltipTypography>,
		},
		{
			title: t("quantity"),
			field: "quantity",
			default: false,
			render: (rawData) => <OverflowTooltipTypography align="right">{`${rawData.quantity}`}</OverflowTooltipTypography>,
		},
		{
			title: t("base-price"),
			field: "basePrice",
			default: false,
			render: (rawData) => (
				<OverflowTooltipTypography
					align="right"
					renderTooltipText={() =>
						renderCurrency(
							rawData.basePrice,
							t(rawData.currencyName || "") + ((rawData?.basePrice || 0) !== 1 ? "s" : ""),
							2,
							2
						)
					}
				>
					{renderCurrency(rawData?.basePrice || 0, rawData?.currency, 2, 2)}
				</OverflowTooltipTypography>
			),
		},
		{
			title: t("refund-rate"),
			field: "finalRefundablePercentage",
			default: false,
			align: "right",
			render: (rawData) => {
				const isAdjusted = !!rawData.adjustedRate;
				const text = `${formatCurrency(isAdjusted ? rawData?.adjustedRate : rawData.refundRate || 0, 2, 2)}%`;

				return isAdjusted ? (
					<AdjustedAmountColumns
						adjustedAmount={rawData.adjustedAmount ? rawData.adjustedAmount : 0}
						text={text}
						adjustedRate={rawData.adjustedRate ? rawData.adjustedRate : 0}
						adjustedBy={rawData?.adjustedBy ? `${rawData?.adjustedBy?.firstName} ${rawData?.adjustedBy?.lastName}` : ""}
						lastAdjustedAt={localeDate(rawData.lastAdjustedAt) || ""}
						adjustmentComment={rawData.adjustmentComment || ""}
						wasAdjustedBefore={isAdjusted}
						originalAmount={rawData.amount || 0}
						currency={rawData.currency || ""}
						canUserViewFinancials={!!rawData.canUserModifyFinancials}
					/>
				) : (
					<OverflowTooltipTypography align="right">{text}</OverflowTooltipTypography>
				);
			},
		},
		{
			title: t("refund-amount"),
			field: "finalAmount",
			default: true,
			align: "right",
			render: (rawData) => {
				const isAdjusted = !!rawData.adjustedAmount && !!rawData.adjustedRate;
				const text = renderCurrency(isAdjusted ? rawData?.adjustedAmount : rawData.amount || 0, rawData?.currency, 2, 2);

				return isAdjusted ? (
					<AdjustedAmountColumns
						adjustedAmount={rawData.adjustedAmount ? rawData.adjustedAmount : 0}
						text={text}
						adjustedRate={rawData.adjustedRate ? rawData.adjustedRate : 0}
						adjustedBy={`${rawData?.adjustedBy?.firstName} ${rawData?.adjustedBy?.lastName}`}
						lastAdjustedAt={localeDate(rawData.lastAdjustedAt)}
						adjustmentComment={rawData.adjustmentComment || ""}
						wasAdjustedBefore={isAdjusted}
						originalAmount={rawData.amount || 0}
						currency={rawData.currency || ""}
						canUserViewFinancials={!!rawData.canUserModifyFinancials}
					/>
				) : (
					<OverflowTooltipTypography
						renderTooltipText={() =>
							renderCurrency(rawData.amount, t(rawData.currencyName || "") + ((rawData?.amount || 0) !== 1 ? "s" : ""), 2, 2)
						}
						align="right"
					>
						{text}
					</OverflowTooltipTypography>
				);
			},
		},
	];
}

const getObjectPropertyValue = (entry: any): string => {
	const [, val] = entry;
	return String(val);
};

const propertyContainsSearchTerm = (input: string) => (o: any) => {
	const val = getObjectPropertyValue(o).toLowerCase();
	const result = val.includes(input);
	return result;
};

export const searchRefund = (input: string) => (entry: any) => {
	const i = input.toLowerCase();
	return Object.entries(entry).some(propertyContainsSearchTerm(i));
};

export default function useRefundColumns(): { columns: Array<RefundColumn> } {
	const { t } = useTranslation();
	const organizationAgreementsStatus = useRecoilValue(organizationAgreementState);
	const memoAgreementStatus = useMemo(() => organizationAgreementsStatus, [organizationAgreementsStatus]);
	const columns = getRefundColumns(t, memoAgreementStatus);
	return { columns };
}
